import React, { useState } from "react";
import dayjs from "dayjs";
import { ArrowRight, ArrowRight2, MessageQuestion, ReceiptItem, Warning2 } from "iconsax-reactjs";
import emptyInvoiceImg from "../../asset/images/emptyinvoice.svg";
import getInitialsMessage from "../../utilities/messages/getInitialsMessage";
import axios from "axios";
import Loader from "../loader";
import { safeCurrencyFormat } from "../../utilities/safeCurrencyFormat";

export default function TransactionDetails({ txn, typeToIcon }) {
    const [isDownloading, setIsDownloading] = useState(false);
    const handleDownloadReceipt = async () => {
        try {
            setIsDownloading(true);
            const response = await axios.get(`/v1/wallets/download/${txn.id}`, {
                responseType: "blob",
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `receipt-${txn.id}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error("Download failed:", err);
            alert("Failed to download receipt.");
        } finally {
            setIsDownloading(false);
        }
    };

    if (!txn) {
        return (
            <div className="text-center flex-1 py-40 min-h-[91vh] mr-6 mt-4 shadow-lg flex flex-col items-center justify-center">
                <img src={emptyInvoiceImg} alt="No transaction selected" className="w-40 mb-4" />
                <h2 className="text-lg font-semibold text-[#101828]">No transaction selected</h2>
                <p className="text-sm text-[#6F6B7B] mt-2">
                    Choose one from the list to view its full details
                </p>
            </div>
        );
    }

    return (
        <div className="flex flex-col p-6 bg-white rounded-tl-xl h-[91vh] md:shadow-lg md:mt-4 md:mr-6 rounded-tr-xl overflow-y-auto">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#F2F3FC] rounded-full flex items-center justify-center text-[#383B56] font-[450] text-[16px]">
                        {txn.transactionType === "BalanceSwap"
                            ? typeToIcon[txn.transactionType] || (
                                <MessageQuestion size="18" color="#6F739B" variant="Bulk" />
                            )
                            : getInitialsMessage(txn?.receiverName || txn?.title)}
                    </div>

                    <div className="max-w-xs break-words">
                        <h2 className="text-[16px] font-[450] mb-0 text-[#101828] break-words">
                            {txn.title}
                        </h2>
                        <p className="text-[14px] font-[350] text-[#6F739B]">
                            {dayjs(txn.time).format("MMMM D, YYYY • h:mm A")}
                        </p>
                    </div>
                </div>
                <div>
                    <p
                        className={`text-[20px] text-end font-[450] ${txn.amount >= 0 ? "text-[#11A811]" : "text-[#161827]"
                            }`}
                    >
                        {txn.amount < 0 && "-"}
                        {safeCurrencyFormat(Math.abs(txn.amount), txn.currency)}
                    </p>
                    <p
                        className={`text-[14px] text-end font-medium ${txn.status === "Completed"
                                ? "text-[#188B18]"
                                : txn.status === "Pending" || txn.status === "Proccessing"
                                    ? "text-orange-500"
                                    : "text-red-500"
                            }`}
                    >
                        {txn.status}
                    </p>
                </div>
            </div>
            <h3 className="text-[12px] font-[500] text-[#6F739B] uppercase mb-2">
                Payment Details
            </h3>
            {/* Details Table */}
            <div className="bg-[#F7F8FD] !p-5 rounded-lg mb-6">
                {(txn.status === "Failed" && txn?.reasonForFailure) && <dl style={{ borderBottom: "1px solid #E5E7EB" }} className="grid grid-cols-2 gap-y-3 mb-2">
                    <dt className="text-[14px] font-[350] text-[#6F739B]">Reason</dt>
                    <dd className="text-[14px] text-[#101828] text-end break-words break-all whitespace-pre-wrap">
                        {txn?.reasonForFailure || "—"}
                    </dd>
                </dl>}
                <dl className="grid grid-cols-2 gap-y-3">
                    {txn.details.map(({ label, value }) => (
                        <React.Fragment key={label}>
                            <dt className="text-[14px] font-[350] text-[#6F739B]">{label}</dt>
                            <dd className="text-[14px] text-[#101828] text-end break-words break-all whitespace-pre-wrap">
                                {value || "—"}
                            </dd>
                        </React.Fragment>
                    ))}
                </dl>
            </div>
            {/* Attachments */}
            {txn.attachments?.length > 0 && (
                <div className="rounded-lg mb-6">
                    <h3 className="text-sm text-[#6F739B] text-[12px] font-[500] uppercase mb-2">
                        Attachments
                    </h3>
                    <ul className="divide-y divide-gray-200 !px-5 !py-2 border ">
                        {txn.attachments.map((att) => (
                            <li key={att.id} className="py-3 flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                    <ReceiptItem size="18" color="#6F739B" variant="Bulk" />
                                    <span className="text-sm text-[#101828]">{att.label}</span>
                                </div>
                                <a
                                    href={att.url}
                                    className="flex items-center gap-1 text-sm text-[#6F00FF]"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <span>
                                        {att.fileName} {att.fileSize ? ` — ${att.fileSize}` : ""}
                                    </span>
                                    <ArrowRight2 size={16} />
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {/* Sticky bottom actions */}
            <div className="mt-auto pt-4 border-t sticky bottom-0 bg-white z-10">
                <div className="md:flex grid gap-2 md:gap-4">
                    <div className="flex-1 group relative">
                        {txn.transactionType !== "TransactionFee" && (
                            <button
                                onClick={!isDownloading ? handleDownloadReceipt : undefined}
                                disabled={isDownloading}
                                className={`w-full py-3 bg-white border border-[#F2F3FC] rounded-lg flex items-center justify-center gap-2
                          ${isDownloading ? "opacity-60 cursor-not-allowed" : "hover:bg-[#F7F8FD]"
                                    }`}
                            >
                                {isDownloading ? (
                                    <Loader size="sm" />
                                ) : (
                                    <>
                                        <ReceiptItem size="18" color="#6F739B" variant="Bulk" />
                                        Download receipt
                                    </>
                                )}
                            </button>
                        )}
                        <span className="absolute left-1/2 -top-8 -translate-x-1/2 bg-[#101828] text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition">
                            Coming soon
                        </span>
                    </div>
                    <div className="flex-1 group relative">
                        <button
                            disabled
                            className="w-full py-3 bg-white border border-gray-200 rounded-lg flex items-center justify-center gap-2 opacity-50 cursor-not-allowed"
                        >
                            <Warning2 size="18" color="#6F739B" variant="Bulk" />
                            Report an issue
                        </button>
                        <span className="absolute left-1/2 -top-8 -translate-x-1/2 bg-[#101828] text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition">
                            Coming soon
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}
